import { FC } from 'react';
import { DealType } from '../../../../enums/DealType';
import { capitalizeAll } from '../../../../services/utils/HelperFunctions';
import { Contract } from '../../../../types/header/Contract';
import { SalesPerson } from '../../../../types/SalesPerson';
import ReactivateDealFormField from './ReactivateDealFormField';
import { StyledInput } from './Style';

type ReactivateDealInfoProps = {
  contract: Contract;
  salesPerson: SalesPerson;
};

const ReactivateDealInfo: FC<ReactivateDealInfoProps> = ({ contract, salesPerson }) => {
  return (
    <div className='col-sm-6'>
      <div className='row start-section first-row'>
        <div className='col-6 col-sm-6'>
          <ReactivateDealFormField
            fieldName='contractStart'
            inputValue={contract.dealDate}
            labelValue='Contract Start'
            isRequired={true}
            isDateField={true}
            hasInputAddOn={false}
          />
        </div>
        <div className='col-6 col-sm-6'>
          {contract?.dealType !== DealType.Cash && (
            <ReactivateDealFormField
              fieldName='contractEnd'
              inputValue={contract.endDate}
              labelValue='Contract End'
              isRequired={true}
              isDateField={true}
              hasInputAddOn={false}
            />
          )}
          {contract?.dealType === DealType.Cash && (
            <ReactivateDealFormField
              fieldName='downPayment'
              inputValue={contract.downPayment}
              labelValue='Total Payment'
              hasInputAddOn={true}
            />
          )}
        </div>
      </div>
      <div className='row end-section first-row underlined'>
        <div className='col-6 col-sm-6'>
          {contract?.dealType !== DealType.Cash && (
            <ReactivateDealFormField
              fieldName='term'
              inputValue={contract.term}
              isInputWrapper={true}
              labelValue='Term'
              max={99}
              min={1}
            />
          )}
          {contract?.dealType === DealType.Cash && (
            <ReactivateDealFormField
              fieldName='cashPrice'
              inputValue={contract.cashPrice}
              labelValue='Selling Price'
              hasInputAddOn={true}
              isRequired={true}
              isReadOnly={true}
            />
          )}
        </div>
        {contract?.dealType !== DealType.Cash && (
          <div className='col-6 col-sm-6'>
            <ReactivateDealFormField
              labelValue={
                contract?.dealType === DealType.Lease ? 'Lease Payment' : 'Retail Payment'
              }
              fieldName='payment'
              inputValue={contract.payment}
              isReadOnly={true}
              isRequired={true}
              hasInputAddOn={true}
              min={1}
            />
          </div>
        )}
        {contract?.dealType === DealType.Finance && (
          <div className='col-6 col-sm-6 nice-checkboxes'>
            <label className='flush-left'>
              <input type='checkbox' disabled name='isPaidOff' checked={contract?.isPaidOff} />
              <em>Contract Paid Off</em>
            </label>
          </div>
        )}
      </div>
      {contract?.dealType !== DealType.Cash && (
        <div className='row start-section vehicle-info'>
          <div className='col-6 col-sm-6'>
            {contract?.dealType === DealType.Lease && (
              <ReactivateDealFormField
                fieldName='annualMiles'
                inputValue={contract.leaseAnnualMiles}
                labelValue='Annual Miles'
                isReadOnly={true}
                isInputWrapper={true}
              />
            )}
            {contract?.dealType === DealType.Finance && (
              <ReactivateDealFormField
                fieldName='apr'
                inputValue={contract.rate}
                labelValue='APR'
                isReadOnly={true}
                isInputWrapper={true}
              />
            )}
          </div>
          <div className='col-6 col-sm-6'>
            {contract?.dealType === DealType.Lease && (
              <ReactivateDealFormField
                fieldName='driveOff'
                inputValue={contract.driveOffAmount}
                labelValue='Drive Off'
                isReadOnly={true}
                hasInputAddOn={true}
              />
            )}
            {contract?.dealType === DealType.Finance && (
              <ReactivateDealFormField
                fieldName='downPayment'
                inputValue={contract.downPayment}
                labelValue='Down Payment'
                isReadOnly={true}
                hasInputAddOn={true}
              />
            )}
          </div>
        </div>
      )}
      {contract?.dealType !== DealType.Cash && (
        <div className='row vehicle-info'>
          <div className='col-6 col-sm-6'>
            {contract?.dealType === DealType.Lease && (
              <ReactivateDealFormField
                fieldName='residual'
                labelValue='Residual'
                inputValue={contract.residual}
                hasInputAddOn={true}
                isReadOnly={true}
              />
            )}
            {contract?.dealType === DealType.Finance && (
              <ReactivateDealFormField
                fieldName='amountFinanced'
                labelValue='Amount Financed'
                inputValue={contract.amountFinanced}
                hasInputAddOn={true}
                isReadOnly={true}
                isRequired={true}
              />
            )}
          </div>
          <div className='col-6 col-sm-6'>
            {contract?.dealType === DealType.Lease && (
              <ReactivateDealFormField
                fieldName='netCapCost'
                labelValue='Net Cap Cost'
                inputValue={contract.netCapCost}
                hasInputAddOn={true}
                isReadOnly={true}
              />
            )}
            {contract?.dealType === DealType.Finance && (
              <ReactivateDealFormField
                fieldName='balloonAmount'
                labelValue='Balloon Amount'
                inputValue={contract.balloonAmount}
                hasInputAddOn={true}
                isReadOnly={true}
                min={0}
                isRequired={true}
              />
            )}
          </div>
        </div>
      )}
      {contract?.dealType === DealType.Lease && (
        <div className='row vehicle-info'>
          <div className='col-6 col-sm-6'>
            <ReactivateDealFormField
              labelValue='Money Factor'
              fieldName='moneyFactor'
              inputValue={contract.rate}
              isInputWrapper={true}
              isReadOnly={true}
              min={0.0}
              max={0.00499}
            />
          </div>
          <div className='col-6 col-sm-6'>
            <ReactivateDealFormField
              labelValue='Cap Cost Reduction'
              fieldName='totalCapReduction'
              inputValue={contract.totalCapReduction}
              isReadOnly={true}
              hasInputAddOn={true}
            />
          </div>
        </div>
      )}
      <div className='end-section vehicle-info'>
        <div className='row vertical'>
          <div className='col-3 text-end'>
            <label>Bank</label>
          </div>
          <div className='col-9'>
            <div className='value-block'>
              <div className='read-only'>
                <div className='input-wrapper'>
                  <StyledInput
                    id='bank'
                    name='bank'
                    className='form-control'
                    placeholder='N/A'
                    readOnly
                    value={contract.bankName}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='row vertical static'>
          <div className='col-3 text-end'>
            <label>Sold By</label>
          </div>
          <div className='col-9'>
            <div className='value-block static'>{capitalizeAll(contract?.soldBy)}</div>
          </div>
        </div>
        <div className='row vertical'>
          <div className='col-3 text-end'>
            <label>Assigned To</label>
          </div>
          <div className='col-7'>
            <div className='value-block'>
              <div className='read-only'>
                <div className='input-wrapper'>
                  <select
                    id='assignedTo'
                    name='assignedTo'
                    className='form-control'
                    disabled
                    value={salesPerson?.name}
                  >
                    <option>{salesPerson?.name}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactivateDealInfo;
